package de.oevbs.kunde.kundenportal2.components

import de.oevbs.kosmos.ui.forms.inputgroup.standard.InputTextScope
import de.oevbs.kosmos.ui.icons.Icon
import de.oevbs.kosmos.ui.icons.MaterialIconsFilled
import de.oevbs.kosmos.ui.icons.MaterialIconsOutlined
import de.oevbs.kosmos.ui.icons.icon
import de.oevbs.kosmos.ui.overlays.modals.BusyTracker
import dev.fritz2.core.*
import dev.fritz2.headless.components.popOver
import dev.fritz2.headless.foundation.utils.floatingui.core.middleware.offset
import dev.fritz2.headless.foundation.utils.floatingui.utils.PlacementValues
import dev.fritz2.headless.validation.Severity
import dev.fritz2.tracking.tracker
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLHRElement

val globalSubmitTracker = tracker()
val globalInitTracker = tracker().also { BusyTracker.attach(it) }

/**
 * Ein für das Kupo gestyltes popover, welches zur Anzeige von Hilfetexten verwendet wird.
 *
 * @param icon Das Icon, das zum Toggeln des Popovers angeclickt werden soll.
 * @param content Der Inhalt des Popovers ohne Container
 */
fun RenderContext.kupoPopover(icon: Icon, content: RenderContext.() -> Unit) {
    popOver {
        popOverButton(classes = "mr-3 mt-2") {
            tabIndex(-1)
            icon("h-5 w-5 text-secondary-500", icon = icon)
        }
        popOverPanel("bg-primary-50 shadow rounded p-3 py-4 w-96 z-10") {
            placement = PlacementValues.bottomEnd
            addMiddleware(offset {
                mainAxis = 15
                crossAxis = 15
            })
            transition(
                opened,
                "transition ease-out duration-200",
                "opacity-0 translate-y-1",
                "opacity-100 translate-y-0",
                "transition ease-in duration-150",
                "opacity-100 translate-y-0",
                "opacity-0 translate-y-1"
            )
            arrow("h-6 w-6")
            content()
        }
    }
}

/**
 * Erweiterung des InputTextScopes aus der Kosmos2 UI Bibliothek, die es erlaubt, ein [kupoPopover] an einem clickbaren Icon
 * innerhalb eines Inputfeldes anzuzeigen.
 *
 * Verwendung: Das inputText wie sonst auch nutzen und in dessen Context das iconClickableWithPopover aufrufen.
 * Ein Container (Karte mit grauem Hintergrund) wird bereits in der Funktion gesetzt, so dass nur noch der Inhalt
 * übergeben werden muss.
 *
 * @param icon Das Icon, das zum Toggeln des Popovers angeclickt werden soll.
 * @param content Der Inhalt des Popovers ohne Container
 */
fun InputTextScope.iconClickableWithPopover(icon: Icon, content: RenderContext.() -> Unit) {
    trailingAddOn {
        kupoPopover(icon, content)
    }
}

fun RenderContext.separator(
    classes: String = ""
): HtmlTag<HTMLDivElement> = div("mx-1 h-px $classes") {}

/**
 * Horizontaler Trenner, der komplett fertig gestyled ist.
 */
fun RenderContext.separator(): HtmlTag<HTMLHRElement> = hr("my-2 border-b border-t-0 border-secondary-200") { }


private const val inlineLinkStyleClasses = "text-error-500 hover:text-primary-500 cursor-pointer"

/**
 * Rendert einen Link mit href in einen Fließtext hinein. Für einen Link mit einem Routing-Target bitte den
 * [textualButton] nutzen.
 */
fun RenderContext.inlineLink(
    text: String,
    link: String,
    target: String = "_blank"
) = a(inlineLinkStyleClasses) {
    icon(
        classes = "size-4 flex-shrink-0 stroke-1 inline",
        icon = MaterialIconsFilled.keyboard_double_arrow_right
    )
    +text
    href(link)
    target(target)
}

/**
 * Erzeugt einen Button, der sich in Fließtexte einbauen lässt. Dieser wird farblich hervorgehoben.
 * Die Factory gibt einen [Flow] zurück, so dass man direkt das passende Handling daran anschließen kann:
 * ```kotlin
 * paragraph {
 *   + "Hier geht es zum "
 *   textualButton("Posteingang") handledBy { Pages.inboxInbox.visit() }
 *   + ". Sie können aber auch..."
 * }
 * ```
 */
fun RenderContext.textualButton(label: String): Flow<Unit> = button(inlineLinkStyleClasses) {
    icon(
        classes = "size-4 flex-shrink-0 stroke-1 inline",
        icon = MaterialIconsFilled.keyboard_double_arrow_right
    )
    +label
}.clicks.map { }

/**
 * Rendert das angegebene Bild und gibt dessen ClickFlow zurück.
 */
fun RenderContext.imageRoute(
    imgClasses: String,
    imgAltText: String,
    imgSrc: String
): Flow<Unit> = img("cursor-pointer $imgClasses") {
    title(imgAltText)
    src(imgSrc)
}.clicks.map {}

fun RenderContext.sectionHeader(text: String): HtmlTag<HTMLDivElement> = sectionHeader(text) {}

fun RenderContext.sectionHeader(text: String, rightSideContent: RenderContext.() -> Unit?): HtmlTag<HTMLDivElement> =
    div("flex flex-col lg:flex-row justify-start lg:justify-between") {
        h4("mt-0 mb-1 hyphens-manual vui-label-3 ") {
            +text
        }
        span {
            rightSideContent()
        }
    }

data class SeveritySettings(
    val icon: Icon,
    val textColor: String,
    val bgColor: String
)

fun SeveritySettings(severity: Severity): SeveritySettings {
    return when (severity) {
        Severity.Success -> {
            SeveritySettings(
                MaterialIconsOutlined.check_circle,
                "text-success-500",
                "bg-success-100"
            )
        }

        Severity.Info -> {
            SeveritySettings(
                MaterialIconsOutlined.info,
                "text-info-500",
                "bg-info-100"
            )
        }

        Severity.Warning -> {
            SeveritySettings(
                MaterialIconsOutlined.error,
                "text-warning-500",
                "bg-warning-100"
            )
        }

        else -> {
            SeveritySettings(
                MaterialIconsOutlined.cancel,
                "text-error-500",
                "bg-error-100"
            )
        }
    }
}